import type {
    SketchConstraintFormulaCompileContext,
    SketchConstraintFormulaCompileResult,
    SketchConstraintFormulaCompiler,
} from '../../../../types/sketch/solver/SketchConstraintFormulaCompiler';
import {SketchParametricEquationFactory} from './SketchParametricEquationFactory';

/**
 * 数值型几何关系公式编译器。
 *
 * 职责：编译不属于显式尺寸对象、但自身携带锁定数值的距离、角度、半径和直径关系。
 * 尺寸关系展示是只读快照，不进入真实关系容器，因此不存在尺寸方程重复计入分支。
 */
export class SketchNumericConstraintFormulaCompiler implements SketchConstraintFormulaCompiler {
    /** 数值关系类型。 */
    public readonly relationKinds = [
        'Distance',
        'Angle',
        'Radius',
        'Diameter',
    ] as const;

    /**
     * 编译数值关系。
     *
     * @param context 编译上下文。
     * @returns 编译结果。
     */
    public compile(
        context: SketchConstraintFormulaCompileContext,
    ): SketchConstraintFormulaCompileResult {
        switch (context.constraintState.relationKind) {
            case 'Distance':
                return this.compileDistance(context);
            case 'Angle':
                return this.compileAngle(context);
            case 'Radius':
                return this.compileRadius(context, false);
            case 'Diameter':
                return this.compileRadius(context, true);
            default:
                return 'not-applicable';
        }
    }

    /**
     * 编译两点距离关系。
     *
     * 当前对象式 `PointDistanceConstraint` 的非尺寸用途使用 aligned 距离；x/y 度量方向
     * 属于尺寸领域并由尺寸公式编译器处理；关系面板的尺寸只读快照不会进入本注册表。
     *
     * @param context 编译上下文。
     * @returns 编译结果。
     */
    private compileDistance(
        context: SketchConstraintFormulaCompileContext,
    ): SketchConstraintFormulaCompileResult {
        const points = context.resolveDirectPointHandles();
        const targetDistance = context.constraintState.numericValue;

        if (points.length !== 2) {
            context.reportUnsupported('距离关系需要两个点目标');
            return 'rejected';
        }
        if (typeof targetDistance !== 'number' || !Number.isFinite(targetDistance) || targetDistance <= 0) {
            context.reportUnsupported('距离关系缺少有效的正距离锁定值');
            return 'rejected';
        }

        context.system.addEquation(SketchParametricEquationFactory.pointPointDistance(
            `constraint:${context.constraintState.constraintId}`,
            points[0]!,
            points[1]!,
            targetDistance,
        ));
        return 'compiled';
    }

    /**
     * 编译两直线有符号角度关系。
     *
     * @param context 编译上下文。
     * @returns 编译结果。
     */
    private compileAngle(
        context: SketchConstraintFormulaCompileContext,
    ): SketchConstraintFormulaCompileResult {
        const entityIds = context.resolveDirectEntityIds();
        const firstLine = context.linePointHandles.get(entityIds[0] ?? '');
        const secondLine = context.linePointHandles.get(entityIds[1] ?? '');
        const lockedSignedAngle = context.constraintState.numericValue;

        if (entityIds.length !== 2 || !firstLine || !secondLine) {
            context.reportUnsupported('角度关系需要两个线实体目标');
            return 'rejected';
        }
        if (typeof lockedSignedAngle !== 'number' || !Number.isFinite(lockedSignedAngle)) {
            context.reportUnsupported('角度关系缺少有效的锁定角度');
            return 'rejected';
        }

        context.system.addEquation(SketchParametricEquationFactory.lineLineAngle(
            `constraint:${context.constraintState.constraintId}`,
            firstLine[0],
            firstLine[1],
            secondLine[0],
            secondLine[1],
            Math.abs(lockedSignedAngle),
            lockedSignedAngle >= 0 ? 1 : -1,
        ));
        return 'compiled';
    }

    /**
     * 编译半径或直径关系。
     *
     * @param context 编译上下文。
     * @param diameter 当前数值是否为直径。
     * @returns 编译结果。
     */
    private compileRadius(
        context: SketchConstraintFormulaCompileContext,
        diameter: boolean,
    ): SketchConstraintFormulaCompileResult {
        const entityIds = context.resolveDirectEntityIds();
        const radiusParameterId = context.radiusParameterIds.get(entityIds[0] ?? '');
        const numericValue = context.constraintState.numericValue;

        if (entityIds.length !== 1 || !radiusParameterId) {
            context.reportUnsupported(`${diameter ? '直径' : '半径'}关系需要一个圆系实体目标`);
            return 'rejected';
        }
        if (typeof numericValue !== 'number' || !Number.isFinite(numericValue) || numericValue <= 0) {
            context.reportUnsupported(`${diameter ? '直径' : '半径'}关系缺少有效的正数锁定值`);
            return 'rejected';
        }

        context.system.addEquation(SketchParametricEquationFactory.circleRadius(
            `constraint:${context.constraintState.constraintId}`,
            radiusParameterId,
            diameter ? numericValue / 2 : numericValue,
        ));
        return 'compiled';
    }
}
